import { useState } from "react";
import { Loader2, ShieldCheck, Lock, User, Satellite, Radio, Waves, AlertTriangle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { LangContext, useT, useLang } from "../lib/i18n";
import brandLogo from "../assets/resilio-logo.webp";
import hero from "../assets/login-hero.webp";

export default function Login() {
  const [lang, setLang] = useState(() => localStorage.getItem("resilio_lang") || "es");
  const changeLang = (l) => {
    localStorage.setItem("resilio_lang", l);
    setLang(l);
  };
  return (
    <LangContext.Provider value={lang}>
      <LoginView changeLang={changeLang} />
    </LangContext.Provider>
  );
}

function LoginView({ changeLang }) {
  const { login } = useAuth();
  const t = useT();
  const lang = useLang();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.login_err);
      setLoading(false);
    }
  };

  const chips = [
    { icon: Satellite, label: "NASA GPM" },
    { icon: Waves, label: "Copernicus Sentinel" },
    { icon: ShieldCheck, label: "SNCZI · MITECO" },
    { icon: Radio, label: "AEMET · SAIH" },
  ];

  return (
    <div className="min-h-screen w-full bg-[#06090E] text-slate-200 flex flex-col lg:flex-row overflow-hidden" data-testid="login-page">
      {/* STORY SIDE */}
      <div className="relative lg:w-[56%] min-h-[320px] lg:min-h-screen overflow-hidden border-b lg:border-b-0 lg:border-r border-slate-800">
        <img src={hero} alt="RESILIO.ES — vigilancia espacial sobre España" className="absolute inset-0 h-full w-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06090E] via-[#06090E]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#06090E]/75" />

        <div className="absolute top-[20%] left-[44%] hidden lg:block" aria-hidden>
          <span className="block h-24 w-24 rounded-full border border-cyan-400/40 animate-ping" style={{ animationDuration: "3.6s" }} />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-between p-8 lg:p-12">
          <div className="reveal" style={{ animationDelay: "40ms" }}>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-mono text-cyan-300 tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
              {lang === "es" ? "SISTEMA OPERATIVO · EN VIVO" : "SYSTEM OPERATIONAL · LIVE"}
            </span>
          </div>

          <div className="max-w-xl">
            <h1 className="text-3xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.05] reveal" style={{ animationDelay: "120ms" }}>
              {t.login_headline}
            </h1>
            <p className="mt-4 text-sm lg:text-base text-slate-300/90 leading-relaxed reveal" style={{ animationDelay: "220ms" }}>
              {t.login_sub}
            </p>
            <div className="mt-6 space-y-2.5">
              {[t.login_point1, t.login_point2, t.login_point3].map((p, i) => (
                <div key={i} className="flex items-start gap-2.5 text-[13px] text-slate-300 reveal" style={{ animationDelay: `${320 + i * 90}ms` }}>
                  <span className="mt-0.5 grid place-items-center h-4 w-4 rounded-full bg-cyan-500/15 border border-cyan-500/40 shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                  </span>
                  {p}
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-2 reveal" style={{ animationDelay: "640ms" }}>
              {chips.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B0F19]/80 border border-slate-700/70 text-[10px] font-mono text-slate-300 backdrop-blur">
                  <Icon className="h-3 w-3 text-cyan-400" /> {label}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 reveal" style={{ animationDelay: "740ms" }}>
            <AlertTriangle className="h-3 w-3 text-amber-400/70" />
            {lang === "es" ? "Uso oficial y exclusivo de autoridades competentes · EPSG:25830" : "Official use, competent authorities only · EPSG:25830"}
          </div>
        </div>
      </div>

      {/* FORM SIDE */}
      <div className="relative lg:w-[44%] flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="absolute top-5 right-6">
          <button
            onClick={() => changeLang(lang === "es" ? "en" : "es")}
            data-testid="login-lang-toggle"
            className="text-[11px] font-mono text-slate-400 hover:text-cyan-300 transition-colors"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <form onSubmit={submit} className="w-full max-w-sm reveal" style={{ animationDelay: "160ms" }} data-testid="login-form">
          <img src={brandLogo} alt="RESILIO.ES" className="h-11 w-auto mb-8" />
          <h2 className="text-lg font-semibold text-white">{t.login_access}</h2>
          <p className="mt-1 text-[13px] text-slate-400">{t.login_access_sub}</p>

          <div className="mt-7 space-y-4">
            <label className="block">
              <span className="text-[11px] font-mono tracking-wider text-slate-400 uppercase">{t.login_user}</span>
              <div className="mt-1.5 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="login-username-input"
                  placeholder="admin"
                  className="w-full bg-[#111726] border border-slate-700 rounded-md pl-9 pr-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/40 transition"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-[11px] font-mono tracking-wider text-slate-400 uppercase">{t.login_password}</span>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  placeholder="••••••••••••"
                  className="w-full bg-[#111726] border border-slate-700 rounded-md pl-9 pr-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/40 transition"
                />
              </div>
            </label>
          </div>

          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2 text-[12px] text-red-300" data-testid="login-error">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-cyan-500/90 hover:bg-cyan-400 text-[#06090E] font-semibold text-sm py-2.5 transition-colors disabled:opacity-60 shadow-[0_0_24px_rgba(8,145,178,.35)]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {loading ? t.login_signing : t.login_submit}
          </button>

          <p className="mt-6 text-[10px] font-mono text-slate-600 leading-relaxed">{t.login_footer}</p>
        </form>
      </div>
    </div>
  );
}
